import moment from 'moment';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  ENSURE_TARIFF_BOLT_ONS,
  INITIAL_AUTHENTICATION,
  LOAD_TARIFF_BOLT_ONS,
  SIM_PROVISION_ADD_TARIFF_BOLT_ON,
  SIM_PROVISION_REMOVE_TARIFF_BOLT_ON,
  SIM_PROVISION_SET_NETWORK_TARIFF,
} from '../../actions/actionTypes';
import {
  ensureTariffBoltOns,
  loadTariffBoltOns,
  loadTariffBoltOnsFailure,
  loadTariffBoltOnsPending,
  loadTariffBoltOnsSuccess,
} from '../../actions/boltOnActions';
import { Action, PromiseAction } from '../../actions/commonActions';
import * as boltOnApi from '../../api/boltOnApi';
import { BoltOnItem, DropdownItem, NonUniqueItem } from '../../components/ui/input/DropdownInput';
import { AddBoltOnPayload, LoadBoltOnsPayload, RemoveBoltOnPayload } from '../../models/payload/loadRequests';
import { ChangeProvisionCommand } from '../../models/state/commands';
import { BoltOnsState, getAccountNetworkKey, SubmissionState } from '../../models/state/simProvision/simProvisionState';
import { selectTariffBoltOns, selectTariffTemporalBoltOns } from '../../selectors/simProvision';
import { selectSubmission } from '../../selectors/submissions';

/*
 * A saga which fetches the list of tariff bolt-ons
 */
export function* loadTariffBoltOnsSaga(action: PromiseAction<LoadBoltOnsPayload, DropdownItem[]>) {
  const payload = action.payload;
  if (!payload) {
    return;
  }

  yield put(loadTariffBoltOnsPending(payload));
  try {
    const boltOns: BoltOnItem[] = yield call(boltOnApi.getBoltOns, payload);
    yield put(loadTariffBoltOnsSuccess(payload, boltOns));
    action.resolve && action.resolve(boltOns);
  } catch (error: any) {
    yield put(loadTariffBoltOnsFailure(payload, error));
    console.error(error);
  }
}

/*
 * A saga which fetches the tariff bolt-ons for the specified command if needed
 */
export function* ensureTariffBoltOnsSaga(action: Action<string>) {
  const commandKey = action.payload;
  if (!commandKey) {
    return;
  }

  const boltOnsState: BoltOnsState = yield select(selectTariffBoltOns);
  const temporalBoltOnsState: BoltOnsState = yield select(selectTariffTemporalBoltOns);
  const submission: SubmissionState = yield select(selectSubmission);
  const command = submission.simCommands.find((cmd: any) => {
    return cmd.key === commandKey;
  });

  if (!command) {
    return;
  }

  switch (command.definitionAlias) {
    case 'migrate-in':
    case 'provision':
    case 'port-in': {
      const { accountId, boltOns, networkId, tariffNumber } = command;
      if (!networkId || !accountId) {
        return;
      }

      yield* ensureBoltOns(boltOnsState, accountId, networkId, tariffNumber, boltOns, false);
      break;
    }
    case 'change-provision': {
      const { accountId, boltOns, networkId, tariffNumber, temporalBoltOns } = command as ChangeProvisionCommand;
      if (!networkId || !accountId) {
        return;
      }

      const allBoltOns = boltOns.concat(temporalBoltOns);
      yield* ensureBoltOns(boltOnsState, accountId, networkId, tariffNumber, allBoltOns, false);
      yield* ensureBoltOns(temporalBoltOnsState, accountId, networkId, tariffNumber, allBoltOns, true);
      break;
    }
  }
}

/*
 * A saga which fetches the tariff bolt-ons for the specified command if needed
 */
export function* boltOnAddedSaga(action: Action<AddBoltOnPayload>) {
  const payload = action.payload;
  if (!payload) {
    return;
  }

  yield* ensureTariffBoltOnsSaga(ensureTariffBoltOns(payload.commandKey));
}

/*
 * A saga which fetches the tariff bolt-ons for the specified command if needed
 */
export function* boltOnRemovedSaga(action: Action<RemoveBoltOnPayload>) {
  const payload = action.payload;
  if (!payload) {
    return;
  }

  yield* ensureTariffBoltOnsSaga(ensureTariffBoltOns(payload.commandKey));
}

function* ensureBoltOns(
  boltOnsState: BoltOnsState,
  accountId: number,
  networkId: number,
  tariffNumber: number | undefined,
  boltOns: NonUniqueItem[],
  isTemporal: boolean,
) {
  const accountNetworkKey = getAccountNetworkKey(accountId, networkId);
  const networkBoltOnsState = boltOnsState[accountNetworkKey];
  const boltOnIds = boltOns.map((b) => b.key).join(',');
  const selectionBoltOnIds = (networkBoltOnsState && networkBoltOnsState.selection.boltOns) || '';
  if (
    !networkBoltOnsState ||
    tariffNumber !== networkBoltOnsState.selection.tariffNumber ||
    boltOnIds !== selectionBoltOnIds ||
    (networkBoltOnsState.availableBoltOns.expiresOn &&
      networkBoltOnsState.availableBoltOns.expiresOn < moment().toDate())
  ) {
    const loadPayload = {
      boltOns: boltOnIds,
      accountId,
      networkId,
      tariffNumber,
    };

    const loadStandardAction = loadTariffBoltOns({
      ...loadPayload,
      isTemporal,
    });
    yield call(loadTariffBoltOnsSaga, loadStandardAction);
  }
}

/*
 * Initializes all sagas that are related to network tariffs
 */
export function* initTariffBoltOnSagas() {
  yield take(INITIAL_AUTHENTICATION);
  yield takeEvery(LOAD_TARIFF_BOLT_ONS, loadTariffBoltOnsSaga);
  yield takeEvery(ENSURE_TARIFF_BOLT_ONS, ensureTariffBoltOnsSaga);
  yield takeEvery(SIM_PROVISION_SET_NETWORK_TARIFF, ensureTariffBoltOnsSaga);
  yield takeEvery(SIM_PROVISION_ADD_TARIFF_BOLT_ON, boltOnAddedSaga);
  yield takeEvery(SIM_PROVISION_REMOVE_TARIFF_BOLT_ON, boltOnRemovedSaga);
}
